import React, { useEffect } from "react";
import PagePanel from "app/components/PagePanel";
import styles from "./styles.module.scss";
import { useForm, SubmitHandler } from "react-hook-form";
import CopyField from "../CopyField";
import { useActions, useSelector } from "app/utils/hooks";
import { RootState } from "typedefs";
import { fetchCoachProfile } from "app/actions/scheduling/coachProfile";
import { Loading } from "app/components/Wrappers";
import { useGoogleLogin } from "@react-oauth/google";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "../../../utils/request/scheduling/fetch";
import CalendarIntegrationModal from "../../../components/modals/CalendarIntegrationModal";
import { Trans, useTranslation } from "react-i18next";
import { icalUrlRegex } from "app/routes/Calendar/EventForm/constants";
import SwitchToggle from "../../../components/OptionButtonGroup/SwitchToggle";
import CalendarSettingsLayout from "../Layout";
import { default as SwitchTogglePublic } from "../SwitchToggle";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import ButtonVariant from "app/components/Button/ButtonVariant";

// Events push into a dedicated "CoachNow" calendar the backend creates in
// the coach's Google account. That is what the calendar.app.created scope
// allows — and because it only reaches calendars this app creates, Google
// classifies it as non-sensitive: no app verification, no "unverified app"
// interstitial. The coach doesn't pick a target calendar.
const PUSH_CALENDAR_NAME = "CoachNow";

// The three record types that can push to Google Calendar. A coach checks
// each independently — there's no "all" default and no special-cased type.
const PUSH_EVENT_TYPE_OPTIONS = [
  { value: "LessonSchedule", label: "Sessions" },
  { value: "CoachScheduleBlock", label: "Blocks" },
  { value: "Program", label: "Programs" },
];

interface FormValues {
  calendarUrl: string;
  publicityCalendarEnabled: boolean;
  publicityCalendarUrl: string;
  calendarSyncEnabled: boolean;
  ignoreFreeEventsEnabled: boolean;
}

export default function CalendarSettings() {
  const [calendarSync, setCalendarSync] = React.useState(false);
  const [calendars, setCalendars] = React.useState([]);
  const [modal, setModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selectedCalendars, setSelectedCalendars] = React.useState([]);
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [iCalUrl, setICalUrl] = React.useState("");
  const [iCalIntegration, setICalIntegration] = React.useState(false);
  const [canPush, setCanPush] = React.useState(false);
  const [pushEventTypes, setPushEventTypes] = React.useState([]);
  // Push is "on" whenever at least one event type is selected — there's no
  // separate master switch, just the three checkboxes below. Without the
  // write grant nothing can push (and the backend rejects push_enabled),
  // whatever types are still stored server-side.
  const pushEnabled = canPush && pushEventTypes.length > 0;
  const [connectingGoogle, setConnectingGoogle] = React.useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = React.useState(false);
  const [savingCalendars, setSavingCalendars] = React.useState(false);
  const [enablingPush, setEnablingPush] = React.useState(false);
  const [refreshingEvents, setRefreshingEvents] = React.useState(false);
  const [savingPublicCalendar, setSavingPublicCalendar] = React.useState(false);
  const [savingIgnoreFreeEvents, setSavingIgnoreFreeEvents] =
    React.useState(false);
  const [connectingIcal, setConnectingIcal] = React.useState(false);
  const [disconnectingIcal, setDisconnectingIcal] = React.useState(false);
  const { t } = useTranslation();
  const history = useHistory();

  const onSuccess = (res: any) => {
    const params = {
      integration: {
        auth_code: res.code,
        name: "google",
        action: "activate",
      },
    };

    schedulingFetchPost(`/coach_profiles/${userId}/integrations`, params)
      .then((res) => {
        setCalendars(res.integration.authInfo?.calendars || []);
        setCanPush(!!res.integration.canPush);
        setModal(true);
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setConnectingGoogle(false));
  };

  // A blocked popup surfaces through onNonOAuthError exactly like a
  // dismissed one, but the user never saw anything — without this branch the
  // "cancelled or failed" toast reads as if they denied consent themselves.
  const popupBlocked = (error?: unknown) =>
    (error as { type?: string } | undefined)?.type === "popup_failed_to_open";

  // Covers the popup being dismissed/blocked/denied — the connecting state
  // (set synchronously in handleGoogleConnect, before the popup opens) needs
  // a reliable reset path even when onSuccess never fires.
  const onConnectDenied = (error?: unknown) => {
    setConnectingGoogle(false);
    toast.error(
      popupBlocked(error)
        ? t(
            "Your browser blocked the Google sign-in popup. Allow popups for this site and try again.",
          )
        : t("Google sign-in was cancelled or failed. Please try again."),
    );
  };

  // The write grant (calendar.app.created) is requested up front so most
  // coaches never see a second consent popup later. Google's consent screen
  // lets a coach uncheck that permission and keep only read access —
  // canPush then comes back false and the integration still activates as
  // read-only sync; enabling push later falls through to the incremental
  // upgradeAccess() hop below. All three scopes are consent-screen-safe:
  // the readonly pair is verified, app.created is non-sensitive.
  const login = useGoogleLogin({
    onSuccess,
    onError: onConnectDenied,
    onNonOAuthError: onConnectDenied,
    flow: "auth-code",
    scope:
      "https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events.owned.readonly https://www.googleapis.com/auth/calendar.app.created",
  });

  // Disables the Connect button for the whole popup lifetime (not just after
  // it resolves), so a double-click can't open two consent popups at once.
  const handleGoogleConnect = () => {
    setConnectingGoogle(true);
    login();
  };

  // Covers all three no-grant outcomes of the incremental hop: consent
  // completed with the write box unchecked, explicit deny, and dismissing
  // the popup.
  const onUpgradeDenied = (error?: unknown) => {
    setEnablingPush(false);
    toast.error(
      popupBlocked(error)
        ? t(
            "Your browser blocked the Google sign-in popup. Allow popups for this site and try again.",
          )
        : t(
            "Google Calendar write access was not granted. Allow it on the Google consent screen to push your events.",
          ),
    );
  };

  // Incremental consent for the write scope: only requested at the moment a
  // coach enables event push, on top of the read-only scopes granted at
  // connect time. Google merges it with the existing grant.
  const onUpgradeSuccess = (res: any) => {
    const params = {
      integration: {
        auth_code: res.code,
        name: "google",
        action: "activate",
      },
    };

    schedulingFetchPost(`/coach_profiles/${userId}/integrations`, params)
      .then((res) => {
        const freshCalendars = res.integration.authInfo?.calendars;
        if (freshCalendars?.length) {
          setCalendars(freshCalendars);
        }

        if (res.integration.canPush) {
          setCanPush(true);
        } else {
          onUpgradeDenied();
        }
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setEnablingPush(false));
  };

  const upgradeAccess = useGoogleLogin({
    onSuccess: onUpgradeSuccess,
    onError: onUpgradeDenied,
    onNonOAuthError: onUpgradeDenied,
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/calendar.app.created",
  });

  const { register, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      calendarUrl: "https://example.com/calendar.ics",
      publicityCalendarEnabled: false,
      publicityCalendarUrl: "https://example.com/public-calendar.ics",
      calendarSyncEnabled: false,
      ignoreFreeEventsEnabled: false,
    },
  });

  const coachProfile = useSelector((state: RootState) => state.coachProfile);
  const userId: any = useSelector((state: RootState) => state.user.self.id);
  const coachProfileActions = useActions({
    fetchCoachProfile,
  });

  const coachIntegrations = (coachId: number) => {
    return schedulingFetchGet(`/coach_profiles/${coachId}/integrations`).then(
      (integrations) => {
        return integrations;
      },
    );
  };

  const hydrateGoogleIntegration = (integration: any) => {
    setCalendars(integration.authInfo?.calendars || []);
    setSelectedCalendars(integration.selectedCalendarIds || []);
    setCanPush(!!integration.canPush);
    // Push off means no types, whatever the server sends: rows saved before
    // the backend enforced that invariant still hold stale types next to
    // push_enabled: false, and hydrating them would show push as on (the
    // checkboxes are what derive pushEnabled) and re-enable it on the next
    // save. Re-checking a box is all it takes to turn push back on.
    setPushEventTypes(
      integration.pushEnabled ? integration.pushEventTypes || [] : [],
    );
    setIsPrivate(!!integration.privateCalendars);
  };

  const refreshGoogleIntegration = () =>
    coachIntegrations(userId).then((integrations) => {
      const google = integrations.find(
        (integration: any) =>
          integration.name === "google" && integration.status === "active",
      );
      if (google) {
        hydrateGoogleIntegration(google);
      }
    });

  // Reopening the modal refetches server truth first, so unsaved edits from a
  // previous open (cancelled changes) never leak into this one.
  const openManageModal = () => {
    refreshGoogleIntegration()
      .then(() => {
        setModal(true);
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      });
  };

  useEffect(() => {
    if (coachProfile.data === null) {
      coachProfileActions.fetchCoachProfile(userId);
    }

    coachIntegrations(userId)
      .then((integrations) => {
        integrations.forEach((integration: any) => {
          if (
            integration.name === "google" &&
            integration.status === "active"
          ) {
            setCalendarSync(true);
            hydrateGoogleIntegration(integration);
          } else if (
            integration.name === "ical" &&
            integration.status === "active"
          ) {
            setICalIntegration(true);
            setICalUrl(integration.userIdentifier);
          }
        });
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      });
  }, []);

  useEffect(() => {
    schedulingFetchGet(`coach_profiles/${userId}`)
      .then((data) => {
        setValue("publicityCalendarEnabled", data["publicCalendar"]);
        setValue("publicityCalendarUrl", data["publicCalendarUrl"]);
        // Backend stores import_free_events (true = import & block, the default);
        // the UI toggle is the inverse: ON = ignore "Free" events.
        setValue(
          "ignoreFreeEventsEnabled",
          !(data["importFreeEvents"] ?? true),
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  if (coachProfile.error) {
    toast.error("You don’t have permission to access this page");
    history.push("/");
    return null;
  }

  if (coachProfile.pending) {
    return (
      <Loading loadType="spinner" isLoading className={styles.loadingSpinner} />
    );
  }

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    console.log(data);
  };
  const isCalendarPublic = watch("publicityCalendarEnabled");

  const handlePublicCalendarChange = async () => {
    setSavingPublicCalendar(true);
    const data = { public_calendar: !isCalendarPublic };
    schedulingFetchPatch(`coach_profiles/${userId}/calendar_settings`, data)
      .then(() => {
        toast.success(t("Update successful."));
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setSavingPublicCalendar(false));
  };

  const handleIgnoreFreeEventsChange = async (newChecked: boolean) => {
    setSavingIgnoreFreeEvents(true);
    // Toggle ON = ignore "Free" events = do not import them.
    const data = { import_free_events: !newChecked };
    schedulingFetchPatch(`coach_profiles/${userId}/calendar_settings`, data)
      .then(() => {
        toast.success(t("Update successful."));
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setSavingIgnoreFreeEvents(false));
  };

  const handleCalendarRefresh = () => {
    setRefreshingEvents(true);
    schedulingFetchPatch(
      `/coach_profiles/${userId}/integrations/refresh_events`,
      {},
    )
      .then(() => {
        toast.success(t("Event refresh in progress."));
      })
      .catch((_) => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setRefreshingEvents(false));
  };

  const removeIntegration = () => {
    setDisconnectingGoogle(true);
    const params = {
      integration: {
        auth_code: "mock_code",
        name: "google",
        action: "inactivate",
      },
    };

    schedulingFetchPost(`/coach_profiles/${userId}/integrations`, params)
      .then(() => {
        setCalendarSync(false);
        setLoading(false);
        setModal(false);
        setSelectedCalendars([]);
        setIsPrivate(false);
        setCanPush(false);
        setPushEventTypes([]);
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setDisconnectingGoogle(false));
  };

  // Closing the modal only disconnects during the initial connect flow —
  // reopened via "Manage Calendars" it must leave the integration alone.
  const closeModal = () => {
    if (modalBusy) {
      return;
    }

    if (calendarSync) {
      setModal(false);
      setLoading(false);
      // The status summary on the page derives from the same state the modal
      // edits, so cancelled changes would show as if saved — restore server
      // truth. Best effort: on failure the summary keeps the last known
      // values until the modal is next opened.
      refreshGoogleIntegration().catch(() => null);
    } else {
      removeIntegration();
    }
  };

  // A coach independently picks which of the three event types push to
  // Google — there's no separate on/off switch, checking/unchecking these
  // is what turns push on and off (see the derived `pushEnabled` above).
  const toggleEventType = (type: string) => {
    const next = pushEventTypes.includes(type)
      ? pushEventTypes.filter((t: string) => t !== type)
      : [...pushEventTypes, type];

    setPushEventTypes(next);
  };

  const handleEnablePush = () => {
    setEnablingPush(true);
    upgradeAccess();
  };

  const handleSync = () => {
    setSavingCalendars(true);
    const params = {
      coach_integration: {
        calendar_ids: selectedCalendars,
        is_private: isPrivate,
        // No target calendar: the backend provisions (and reuses) the
        // dedicated CoachNow calendar whenever push is enabled.
        push_enabled: pushEnabled,
        push_event_types: pushEventTypes,
      },
    };

    schedulingFetchPatch(`coach_profiles/${userId}/integrations`, params)
      .then(() => {
        setLoading(true);
        setCalendarSync(true);
      })
      .catch(() => {
        // Keep the modal open so the coach can see their choices didn't save.
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setSavingCalendars(false));
  };

  const handleIcalConnect = () => {
    setConnectingIcal(true);
    const params = {
      integration: {
        user_identifier: iCalUrl,
        name: "ical",
        action: "activate",
      },
    };

    schedulingFetchPost(`coach_profiles/${userId}/integrations`, params)
      .then(() => {
        setLoading(true);
        setModal(true);
        setICalIntegration(true);
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setConnectingIcal(false));
  };

  const handleIcalDisconnect = () => {
    setDisconnectingIcal(true);
    const params = {
      integration: {
        user_identifier: iCalUrl,
        name: "ical",
        action: "inactivate",
      },
    };

    schedulingFetchPost(`/coach_profiles/${userId}/integrations`, params)
      .then(() => {
        setICalIntegration(false);
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => setDisconnectingIcal(false));
  };

  const calendarName = (id: string) =>
    calendars.find((calendar) => calendar.id === id)?.summary;
  const syncedCalendarNames = selectedCalendars
    .map(calendarName)
    .filter(Boolean)
    .join(", ");
  const pushedTypeLabels = PUSH_EVENT_TYPE_OPTIONS.filter((option) =>
    pushEventTypes.includes(option.value),
  )
    .map((option) => t(option.label))
    .join(", ");

  // Either direction on its own is a valid connection — import-only, or
  // push-only. Neither is not: it saves a connection that imports nothing and
  // exports nothing, and a coach who wants that disconnects instead
  // (CFTB-3415). The push target needs no validity check here — the backend
  // provisions the CoachNow calendar on save.
  const importsSomething = selectedCalendars.length > 0;
  const nextDisabled = !(importsSomething || pushEnabled);

  const modalBusy = savingCalendars || disconnectingGoogle || enablingPush;

  const buttons = (
    <div className={styles.buttons_container}>
      <button
        className={styles.cancelButton}
        onClick={closeModal}
        disabled={modalBusy}
      >
        {t("Cancel")}
      </button>
      <button
        style={{
          cursor: nextDisabled || modalBusy ? "not-allowed" : "pointer",
          // --brand-color is not defined on this page's ancestry, and an
          // unresolvable var() leaves the button transparent — white text on
          // a white modal. Keep the var for themed contexts, but always give
          // it the concrete brand blue as fallback.
          backgroundColor:
            nextDisabled || modalBusy
              ? "#c2c8d8"
              : "var(--brand-color, #039be5)",
        }}
        disabled={nextDisabled || modalBusy}
        className={styles.nextButton}
        onClick={handleSync}
      >
        {savingCalendars ? t("Saving...") : t("Save")}
      </button>
    </div>
  );

  const handleCheckboxChange = (calendarId: string) => {
    const isSelected = selectedCalendars.includes(calendarId);
    if (isSelected) {
      setSelectedCalendars(selectedCalendars.filter((id) => id !== calendarId));
    } else {
      setSelectedCalendars([...selectedCalendars, calendarId]);
    }
  };

  const extendCheckbox = (label: string, value: string) => {
    return (
      <label key={value} className={styles.wrapper}>
        {label}
        <input
          type="checkbox"
          style={{ cursor: "pointer" }}
          checked={selectedCalendars.includes(value)}
          onChange={() => handleCheckboxChange(value)}
        />
        <span className={styles.checkmark}></span>
      </label>
    );
  };

  // Just closes: the saved selections stay in state so reopening the modal
  // via "Manage Calendars" shows what was persisted.
  const resetForm = () => {
    setLoading(false);
    setModal(false);
  };

  const loadingContent = () => (
    <div className={styles.loading_container}>
      <div className={styles.loading_content_container}>
        <div className={styles.loading_text}>
          <span className={styles.loading_title}>
            {t("Your calendar sync is in progress")}
          </span>
          <span className={styles.loading_subtitle}>
            {t(
              "It may take a short while until you can see all appointments in the calendar.",
            )}
          </span>
        </div>
      </div>
      <button className={styles.loading_button} onClick={resetForm}>
        {t("Ok, got It")}
      </button>
    </div>
  );

  const modalContent = () => (
    <div className={styles.content_container}>
      <div className={styles.content_title}>{t("Import Preferences")}</div>
      <div className={styles.content_text}>
        {t(
          "External calendar appointments will be imported into your calendar, making these times unavailable for bookings.",
        )}
      </div>
      <SwitchToggle
        checked={isPrivate}
        onChange={() => setIsPrivate(!isPrivate)}
        title=""
        customText={t(
          "Keep my external calendar private. Make Google Calendar event information visible to me only.",
        )}
      />
      <div className={styles.modalHorizontalLine} />
      <div className={styles.option_title}>{t("Synced Calendars")}</div>
      <div className={styles.calendar_list_container}>
        <div className={styles.calendar_options_container}>
          <div className={styles.option_text_container}>
            {t("Select at least one of your calendars.")}
          </div>
          <div className={styles.checkbox_container}>
            {calendars.map((calendar) =>
              extendCheckbox(calendar.summary, calendar.id),
            )}
          </div>
        </div>
      </div>
      <div className={styles.modalHorizontalLine} />
      <div className={styles.option_title}>{t("Export Preferences")}</div>
      {canPush ? (
        <div>
          <div className={styles.customText}>
            {t(
              "Push these to a dedicated CoachNow calendar in your Google Calendar:",
            )}
          </div>
          {PUSH_EVENT_TYPE_OPTIONS.map(({ value, label }) => (
            <label key={value} className={styles.wrapper}>
              {t(label)}
              <input
                type="checkbox"
                style={{ cursor: "pointer" }}
                checked={pushEventTypes.includes(value)}
                onChange={() => toggleEventType(value)}
              />
              <span className={styles.checkmark}></span>
            </label>
          ))}
        </div>
      ) : (
        <div className={styles.pushPermissionRow}>
          <span className={styles.customText}>
            {t(
              "Push my CoachNow sessions, blocks and programs to a dedicated CoachNow calendar in your Google Calendar. Requires an extra Google permission.",
            )}
          </span>
          <ButtonVariant
            title={enablingPush ? t("Enabling...") : t("Enable")}
            buttonType={"primary-outline"}
            onClickFunc={handleEnablePush}
            disabled={enablingPush}
          />
        </div>
      )}
    </div>
  );

  const isIcalDisabled =
    (iCalUrl && !icalUrlRegex.test(iCalUrl)) ||
    !iCalUrl ||
    iCalUrl.trim() === coachProfile.data.calendarSubscriptionUrl.trim();

  return (
    <CalendarSettingsLayout>
      <div className={styles.insideContainer}>
        <PagePanel
          title={t("Calendar Integrations")}
          titleStyles={styles.page_title}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.inputContainer}>
              <label htmlFor="calendarUrl" className={styles.labelStyle}>
                {t("Subscribe to your CoachNow Calendar")}
              </label>
              <div style={{ marginBottom: "20px", marginTop: "10px" }}>
                <div className={styles.customText}>
                  {t(
                    "Copy the URL so you can paste it in your preferred calendar app.",
                  )}
                </div>
                <div className={styles.customText}>
                  <Trans
                    i18nKey="Sessions and unavailable times will be synced with your preferred calendar."
                    components={{ strong: <strong /> }}
                  />
                </div>
                <div className={styles.cn_calendar_url}>
                  <CopyField
                    value={coachProfile.data.calendarSubscriptionUrl}
                  />
                </div>
              </div>

              <div className={styles.infoTextContainer}>
                <div className={styles.infoTextContainer}>
                  <label htmlFor="calendarUrl" className={styles.labelStyle}>
                    {t("Google Calendar")}
                  </label>

                  <div className={styles.calendarTextContainer}>
                    {t(
                      "Connect your Google Calendar to sync events, prevent double booking, and manage your workday from one central hub.",
                    )}
                  </div>

                  {calendarSync && (
                    <div className={styles.integrationStatusContainer}>
                      <div className={styles.integrationStatusRow}>
                        <i className="ico ico-calendar" />
                        <span>
                          {t("Syncing from")}:{" "}
                          <strong>
                            {syncedCalendarNames || t("No calendars selected")}
                          </strong>
                        </span>
                      </div>
                      <div className={styles.integrationStatusRow}>
                        <i
                          className={
                            pushEnabled ? "ico ico-check-o" : "ico ico-lock-o"
                          }
                        />
                        <span>
                          {pushEnabled ? (
                            <>
                              {t("Pushing")} {pushedTypeLabels} {t("to your")}{" "}
                              <strong>{PUSH_CALENDAR_NAME}</strong>{" "}
                              {t("calendar")}
                            </>
                          ) : (
                            t("Not pushing to Google Calendar")
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className={styles.connectButtonContainer}>
                    {calendarSync && (
                      <ButtonVariant
                        title={t("Manage Calendars")}
                        buttonType={"primary-outline"}
                        onClickFunc={openManageModal}
                        buttonStyles={styles.button_refresh}
                        disabled={disconnectingGoogle}
                      />
                    )}
                    {calendarSync && (
                      <ButtonVariant
                        title={
                          refreshingEvents ? t("Refreshing...") : t("Refresh")
                        }
                        icon={<i className="ico ico-repeat" />}
                        buttonType={"primary-outline"}
                        onClickFunc={handleCalendarRefresh}
                        buttonStyles={styles.button_refresh}
                        disabled={refreshingEvents || disconnectingGoogle}
                      />
                    )}
                    <ButtonVariant
                      title={
                        calendarSync
                          ? disconnectingGoogle
                            ? t("Disconnecting...")
                            : t("Disconnect")
                          : connectingGoogle
                            ? t("Connecting...")
                            : t("Connect")
                      }
                      buttonType={
                        calendarSync ? "secondary-outline-danger" : "primary"
                      }
                      onClickFunc={
                        calendarSync ? removeIntegration : handleGoogleConnect
                      }
                      buttonStyles={calendarSync ? "" : styles.button_connect}
                      disabled={
                        calendarSync ? disconnectingGoogle : connectingGoogle
                      }
                    />
                  </div>
                  <div className={styles.customTextSmall}>
                    {t(
                      "CoachNow use and transfer to any other app of information received from Google APIs will adhere to ",
                    )}
                    <a
                      href="https://developers.google.com/terms/api-services-user-data-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("Google APIs Services User Data Policy")}
                    </a>
                    {t(", including the Limited Use requirements.")}
                  </div>
                  {modal && (
                    <CalendarIntegrationModal
                      buttons={loading ? null : buttons}
                      showModal={closeModal}
                      closeIcon={!loading && !modalBusy}
                    >
                      {loading ? loadingContent() : modalContent()}
                    </CalendarIntegrationModal>
                  )}
                </div>

                <div className={styles.infoTextContainer}>
                  <label htmlFor="calendarUrl" className={styles.labelStyle}>
                    {t("iCal URL")}
                  </label>

                  <div className={styles.iCalTextContainer}>
                    {t(
                      "Connect your iCalendar to sync events, prevent double booking, and manage your workday from one central hub.",
                    )}
                  </div>
                  <div className={styles.iCalInputContainer}>
                    <div className={styles.iCalInput}>
                      <input
                        placeholder={t("Insert iCal URL")}
                        value={iCalUrl}
                        disabled={iCalIntegration}
                        onChange={(e) => setICalUrl(e.target.value)}
                      />
                    </div>
                    {!iCalIntegration && (
                      <ButtonVariant
                        title={
                          connectingIcal ? t("Connecting...") : t("Connect")
                        }
                        buttonType={"primary"}
                        onClickFunc={handleIcalConnect}
                        buttonStyles={styles.button_connect}
                        disabled={isIcalDisabled || connectingIcal}
                      />
                    )}

                    {iCalIntegration && (
                      <ButtonVariant
                        title={
                          disconnectingIcal
                            ? t("Disconnecting...")
                            : t("Disconnect")
                        }
                        buttonType="secondary-outline-danger"
                        onClickFunc={handleIcalDisconnect}
                        disabled={disconnectingIcal || connectingIcal}
                      />
                    )}

                    {iCalIntegration && (
                      <ButtonVariant
                        title={
                          connectingIcal ? t("Refreshing...") : t("Refetch")
                        }
                        buttonType="secondary-outline-danger"
                        onClickFunc={handleIcalConnect}
                        disabled={connectingIcal || disconnectingIcal}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.horizontalLine} />
            <div className={styles.toggleContainerRows}>
              <SwitchTogglePublic
                defaultChecked={watch("ignoreFreeEventsEnabled")}
                title={
                  savingIgnoreFreeEvents
                    ? t("Saving...")
                    : t('Ignore "Free" calendar events')
                }
                disabled={savingIgnoreFreeEvents}
                customText={t(
                  'Events marked "Free" on your Google Calendar or iCal (such as all-day birthdays or reminders) will block your availability when imported. Toggle On to ignore those events and keep the times open for booking.',
                )}
                register={register}
                name="ignoreFreeEventsEnabled"
                setValue={setValue}
                defaultValue={false}
                onChange={handleIgnoreFreeEventsChange}
              />
            </div>
            <div className={styles.horizontalLine} />
            <label htmlFor="publicityCalendar" className={styles.labelStyle}>
              {t("Publicly Available Calendar")}
            </label>
            <div style={{ marginBottom: "20px", marginTop: "10px" }}>
              <div className={styles.customText}>
                {t(" Copy the URL, so you can link it from your website. ")}
              </div>
              <div className={styles.customText}>
                {t(
                  "Anyone will be able to view your availability and book sessions.",
                )}
              </div>
            </div>
            <div className={styles.toggleContainerRows}>
              <SwitchTogglePublic
                defaultChecked={watch("publicityCalendarEnabled")}
                title={
                  savingPublicCalendar
                    ? t("Saving...")
                    : t("Enable Public Calendar")
                }
                disabled={savingPublicCalendar}
                register={register}
                name="publicityCalendarEnabled"
                setValue={setValue}
                defaultValue={false}
                onChange={handlePublicCalendarChange}
              />
              {isCalendarPublic && (
                <CopyField value={watch("publicityCalendarUrl")} />
              )}
            </div>
          </form>
        </PagePanel>
      </div>
    </CalendarSettingsLayout>
  );
}
